// src/reducers/arcTemplatesReducer.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiClient } from '@center-technologies/shared-api-clients';
import type { ArcTemplateRow } from 'types/schema';
import type { ArcTemplateDefinition } from 'types/arc';
import type { UnifiedFilters } from 'types/filters';
import {
  fetchPublishedArcTemplate,
  createArcTemplate as createArcTemplateApi,
  updateArcTemplate as updateArcTemplateApi,
  type UpdateArcTemplatePayload,
} from '@center-platform/spheres/identity';

interface FetchArcTemplateArgs {
  systemName: string;
  apiClient: ApiClient;
}

// Thunk: fetch a single arc template with request deduplication
export const fetchArcTemplate = createAsyncThunk<
  ArcTemplateRow,
  FetchArcTemplateArgs,
  { rejectValue: string }
>(
  'arcTemplates/fetchArcTemplate',
  async ({ systemName, apiClient }, thunkAPI) => {
    try {
      // Use the existing utility function which fetches the latest published version
      const template = await fetchPublishedArcTemplate(apiClient, systemName);
      return template;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const detailedError = `Failed to fetch arc template: ${systemName} - ${errorMessage}`;
      console.error('Error fetching arc template:', systemName, '-', err);
      return thunkAPI.rejectWithValue(detailedError);
    }
  },
  {
    // Condition function to prevent duplicate requests
    condition: ({ systemName }, { getState }) => {
      const state = getState() as { arcTemplates: ArcTemplatesState };

      // Don't fetch if already loading (request deduplication)
      if (state.arcTemplates.loading[systemName]) {
        return false;
      }

      // Don't fetch if already cached with recent data (within 24 hours)
      const entry = state.arcTemplates.templates[systemName];
      if (entry) {
        const age = Date.now() - entry.fetchedAt;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        if (age < maxAge) {
          return false;
        }
      }

      return true;
    }
  }
);

// Thunk: create a new arc template draft (EAINT-951 D — the canvas editor's only creation
// entry point, always POSTs isPublished:false; publishing is a separate explicit action).
export interface CreateArcTemplateThunkArgs {
  apiClient: ApiClient;
  name: string;
  systemName: string;
  definition: ArcTemplateDefinition;
  applicability?: UnifiedFilters | Record<string, unknown>;
  isActive?: boolean;
}

/** Why a create was refused. `conflict` is the route's 409 — the slug of the chosen name is
 * already registered in the caller's organization — which the gallery answers with its own
 * copy and a suggested rename; everything else is a generic failure. `message` is the
 * developer-facing text kept for the slice's `errors` map and logs, never rendered. */
export interface CreateArcTemplateRejection {
  reason: 'conflict' | 'error';
  message: string;
}

export const createArcTemplate = createAsyncThunk<
  ArcTemplateRow,
  CreateArcTemplateThunkArgs,
  { rejectValue: CreateArcTemplateRejection }
>('arcTemplates/createArcTemplate', async ({ apiClient, ...payload }, thunkAPI) => {
  try {
    const { arcTemplate } = await createArcTemplateApi(apiClient, payload);
    return arcTemplate;
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
    const status = (err as { response?: { status?: number } } | null)?.response?.status;
    return thunkAPI.rejectWithValue({
      reason: status === 409 ? 'conflict' : 'error',
      message: `Failed to create arc template: ${payload.systemName} - ${errorMessage}`,
    });
  }
});

// Thunk: save an in-progress draft version (applicability/definition/name edits).
export interface SaveArcTemplateThunkArgs {
  apiClient: ApiClient;
  systemName: string;
  version: number;
  payload: UpdateArcTemplatePayload;
}

export const saveArcTemplate = createAsyncThunk<
  ArcTemplateRow,
  SaveArcTemplateThunkArgs,
  { rejectValue: string }
>('arcTemplates/saveArcTemplate', async ({ apiClient, systemName, version, payload }, thunkAPI) => {
  try {
    const { arcTemplate } = await updateArcTemplateApi(apiClient, systemName, version, payload);
    return arcTemplate;
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
    return thunkAPI.rejectWithValue(
      `Failed to save arc template: ${systemName} v${version} - ${errorMessage}`
    );
  }
});

// Thunk: publish a draft version — the editor's own explicit "Publish" action, distinct from
// every other save (never fires implicitly from a field edit).
export interface PublishArcTemplateThunkArgs {
  apiClient: ApiClient;
  systemName: string;
  version: number;
}

export const publishArcTemplate = createAsyncThunk<
  ArcTemplateRow,
  PublishArcTemplateThunkArgs,
  { rejectValue: string }
>('arcTemplates/publishArcTemplate', async ({ apiClient, systemName, version }, thunkAPI) => {
  try {
    const { arcTemplate } = await updateArcTemplateApi(apiClient, systemName, version, {
      isPublished: true,
    });
    return arcTemplate;
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
    return thunkAPI.rejectWithValue(
      `Failed to publish arc template: ${systemName} v${version} - ${errorMessage}`
    );
  }
});

interface ArcTemplateEntry {
  template: ArcTemplateRow;
  fetchedAt: number; // Timestamp for potential cache invalidation
}

interface ArcTemplatesState {
  templates: Record<string, ArcTemplateEntry>; // Keyed by systemName
  loading: Record<string, boolean>; // Track loading state per template
  errors: Record<string, string | null>; // Track errors per template
}

const initialState: ArcTemplatesState = {
  templates: {},
  loading: {},
  errors: {},
};

const arcTemplatesSlice = createSlice({
  name: 'arcTemplates',
  initialState,
  reducers: {
    // Clear a specific template from cache (for invalidation)
    clearTemplate: (state, action: PayloadAction<string>) => {
      const systemName = action.payload;
      delete state.templates[systemName];
      delete state.loading[systemName];
      delete state.errors[systemName];
    },
    // Clear all templates (e.g., on logout)
    clearAllTemplates: (state) => {
      state.templates = {};
      state.loading = {};
      state.errors = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArcTemplate.pending, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = true;
        state.errors[systemName] = null;
      })
      .addCase(fetchArcTemplate.fulfilled, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = false;
        state.templates[systemName] = {
          template: action.payload,
          fetchedAt: Date.now(),
        };
        state.errors[systemName] = null;
      })
      .addCase(fetchArcTemplate.rejected, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = false;
        state.errors[systemName] = action.payload ?? `Failed to fetch arc template: ${systemName}`;
      })
      .addCase(createArcTemplate.pending, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = true;
        state.errors[systemName] = null;
      })
      .addCase(createArcTemplate.fulfilled, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = false;
        state.templates[systemName] = { template: action.payload, fetchedAt: Date.now() };
        state.errors[systemName] = null;
      })
      .addCase(createArcTemplate.rejected, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = false;
        state.errors[systemName] = action.payload?.message ?? `Failed to create arc template: ${systemName}`;
      })
      .addCase(saveArcTemplate.pending, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = true;
        state.errors[systemName] = null;
      })
      .addCase(saveArcTemplate.fulfilled, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = false;
        state.templates[systemName] = { template: action.payload, fetchedAt: Date.now() };
        state.errors[systemName] = null;
      })
      .addCase(saveArcTemplate.rejected, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = false;
        state.errors[systemName] = action.payload ?? `Failed to save arc template: ${systemName}`;
      })
      .addCase(publishArcTemplate.pending, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = true;
        state.errors[systemName] = null;
      })
      .addCase(publishArcTemplate.fulfilled, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = false;
        state.templates[systemName] = { template: action.payload, fetchedAt: Date.now() };
        state.errors[systemName] = null;
      })
      .addCase(publishArcTemplate.rejected, (state, action) => {
        const systemName = action.meta.arg.systemName;
        state.loading[systemName] = false;
        state.errors[systemName] = action.payload ?? `Failed to publish arc template: ${systemName}`;
      });
  },
});

export const { clearTemplate, clearAllTemplates } = arcTemplatesSlice.actions;

// Selectors
export const selectArcTemplate = (state: { arcTemplates: ArcTemplatesState }, systemName: string) =>
  state.arcTemplates.templates[systemName]?.template;

export const selectArcTemplateLoading = (state: { arcTemplates: ArcTemplatesState }, systemName: string) =>
  state.arcTemplates.loading[systemName] ?? false;

export const selectArcTemplateError = (state: { arcTemplates: ArcTemplatesState }, systemName: string) =>
  state.arcTemplates.errors[systemName];

// Helper selector to check if a template is cached and fresh (optional TTL)
export const isArcTemplateCached = (
  state: { arcTemplates: ArcTemplatesState },
  systemName: string,
  maxAgeMs: number = 24 * 60 * 60 * 1000 // Default 24 hours
) => {
  const entry = state.arcTemplates.templates[systemName];
  if (!entry) return false;

  const age = Date.now() - entry.fetchedAt;
  return age < maxAgeMs;
};

export default arcTemplatesSlice.reducer;
